import axios from 'axios'
import Button from 'ext-ui/components/Button/Button'
import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router'
import { AnalyticsPageName } from 'src/analytics/analytics.models'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import * as style from 'src/app/CardFeedDetails/CardFeedDetails.style'
import { CardholderSelection } from 'src/app/CardFeedDetails/components/AssignCardholderDropdown'
import CardFeedCardsTable from 'src/app/CardFeedDetails/components/CardFeedCardsTable'
import CardFeedDetailsHeaderTitle from 'src/app/CardFeedDetails/components/CardFeedDetailsHeaderTitle'
import { toRecipient } from 'src/app/CardFeedDetails/utils/toRecipient'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import {
  ReinviteCardholderErrorToastMessage,
  ReinviteCardholderSuccessToastMessage,
} from 'src/app/Toast/Toast'
import AssignAccountOwnerFlyout from 'src/component-flyout/AssignAccountOwnerFlyout/AssignAccountOwnerFlyout'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import ConfirmCardAssignmentModal, {
  AssignmentRecipient,
} from 'src/component-modal/ConfirmCardAssignmentModal/ConfirmCardAssignmentModal'
import ConfirmUnassignCardModal from 'src/component-modal/ConfirmUnassignCardModal/ConfirmUnassignCardModal'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import { useCurrentBrand } from 'src/context/ConfigContext/useConfig'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { OrganizationRoles } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { CardFeedDetailsMatchParams } from 'src/navigation/routeProps'
import {
  cardsPhysicalCardsUrl,
  createCardFeedUrl,
  homeUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import {
  assignedUserDisplayName,
  CardFeedCard,
  getCardFeedDisplayName,
} from 'src/queries/cardFeeds/cardFeeds.models'
import { useReinviteCardholder } from 'src/queries/cardFeeds/domain/cardFeeds.queries'
import useQueryCardFeed from 'src/queries/cardFeeds/domain/useQueryCardFeed'
import useQueryCardFeedCards from 'src/queries/cardFeeds/domain/useQueryCardFeedCards'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'

const CardFeedDetails: React.FC = () => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const history = useHistory()
  const { id } = useParams<CardFeedDetailsMatchParams>()

  const { data: featuresData, isLoading: isFeaturesLoading } =
    useQueryUserFeatures([UserFeature.BULK_CREDIT_CARD_ENROLLMENT])
  const bulkCreditCardEnrollmentEnabled =
    featuresData?.BULK_CREDIT_CARD_ENROLLMENT ?? false
  const { enable_card_feeds: cardFeedsEnabled } =
    useSelector(selectFeatureFlaggerFromState) ?? {}

  const isAllowed = bulkCreditCardEnrollmentEnabled && !!cardFeedsEnabled

  const user = useCurrentUser()
  const {
    data: feedData,
    isLoading: isFeedLoading,
    isError: isFeedError,
    error: feedError,
  } = useQueryCardFeed(user?.organizationId, id, isAllowed)
  const feedName = getCardFeedDisplayName(feedData, translate)

  // A 404 means the feed doesn't exist — route the user into creation so they
  // can add one. Other errors (network, 5xx) fall through to an error state
  // below rather than dumping the user into create on a transient outage.
  const isFeedNotFound =
    isFeedError &&
    axios.isAxiosError(feedError) &&
    feedError.response?.status === 404

  useEffect(() => {
    if (isFeaturesLoading) return
    if (!isAllowed) {
      history.replace(homeUrl())
    }
  }, [isAllowed, isFeaturesLoading, history])

  useEffect(() => {
    if (!isAllowed || !isFeedNotFound) return
    analytics.track('card feed details', { action: 'not found redirect' })
    history.replace(createCardFeedUrl())
  }, [isAllowed, isFeedNotFound, analytics, history])

  useEffect(() => {
    if (!isAllowed) return
    analytics.page(AnalyticsPageName.OrgSettingsCardFeedDetailsTab)
    analytics.track('card feed details - viewed')
  }, [analytics, isAllowed])

  // Hold the gate with a full-page loader while the feature query resolves —
  // otherwise we flash a blank screen before either the page or the redirect.
  if (isFeaturesLoading) {
    return <LoadingBackgroundPage />
  }

  if (!isAllowed || isFeedNotFound) {
    return null
  }

  // Feed query still in flight: show the loader rather than the page chrome
  // with a placeholder title, until we know the real feed name.
  if (isFeedLoading) {
    return <LoadingBackgroundPage />
  }

  // Non-404 errors: show an error state instead of redirecting, so a transient
  // failure doesn't bounce the user into the create flow.
  if (isFeedError) {
    return (
      <>
        <Helmet>
          <title>{translate('Card Feed')}</title>
        </Helmet>
        <LayoutPage title={<span>{feedName}</span>}>
          <div
            role="alert"
            css={style.errorState}
            data-testid="card-feed-details-error"
          >
            {translate(
              'We couldn’t load this card feed. Please refresh the page or try again later.',
            )}
          </div>
        </LayoutPage>
      </>
    )
  }

  // Not loading and not errored, so the feed resolved — the guard also narrows
  // feedData to non-null for the header below.
  if (!feedData) {
    return <LoadingBackgroundPage />
  }

  const title = (
    <CardFeedDetailsHeaderTitle
      name={feedName}
      status={feedData.status}
      referenceId={feedData.referenceId}
      cardFeedId={id}
      organizationId={user?.organizationId}
    />
  )

  return (
    <CardFeedDetailsLoaded
      id={id}
      title={title}
      isActive={feedData.status === 'ACTIVE'}
      isPending={feedData.status === 'SUBMITTED'}
      isFailed={feedData.status === 'FAILED'}
    />
  )
}

interface CardFeedDetailsLoadedProps {
  id: string
  title: React.ReactNode
  // Only a connected (ACTIVE) feed can have cards assigned, so the "Assign All"
  // action is hidden for every non-active state (pending, failed, disconnected).
  isActive: boolean
  // A pending (SUBMITTED) feed has no cards and no connected cards page yet, so
  // the table shows the "connecting" empty state and the cards-page subtitle is
  // hidden until the feed is active.
  isPending: boolean
  // A failed feed never connected and has no cards. The table shows the "error
  // connecting" empty state and the cards-page subtitle is hidden.
  isFailed: boolean
}

const CardFeedDetailsLoaded: React.FC<CardFeedDetailsLoadedProps> = ({
  id,
  title,
  isActive,
  isPending,
  isFailed,
}) => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const dispatch = useDispatch()
  const user = useCurrentUser()
  const reinviteCardholder = useReinviteCardholder()
  // React Query dedupes — calling the same hook here and inside the table
  // shares the same network request and cache entry. Avoids lifting all the
  // table's UI state up just to read the unassigned set for "Assign All".
  // Pending and failed feeds have no cards, so skip the request entirely.
  const {
    cards: cardsData,
    isLoading: isCardsLoading,
    isError: isCardsError,
  } = useQueryCardFeedCards(id, !isPending && !isFailed)
  const unassignedCards = cardsData.filter((c) => c.status === 'UNASSIGNED')
  // Assign All is only actionable when we have data and at least one
  // unassigned card. Loading and error states both disable the button so
  // users don't see a permanently-disabled action with no explanation —
  // the table itself surfaces the error/loading row.
  const canAssign =
    !isCardsLoading && !isCardsError && unassignedCards.length > 0

  const [flyoutCards, setFlyoutCards] = useState<CardFeedCard[]>([])
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false)
  const [selectionResetSignal, setSelectionResetSignal] = useState(0)
  const [selectAllSignal, setSelectAllSignal] = useState<number | undefined>(
    undefined,
  )
  // Single-row dropdown picks bypass the flyout and open the confirm modal
  // directly — the recipient was already chosen inline in the row.
  const [confirmState, setConfirmState] = useState<{
    cards: CardFeedCard[]
    recipient: AssignmentRecipient
  } | null>(null)
  const [unassignCard, setUnassignCard] = useState<CardFeedCard | null>(null)

  const openFlyoutWith = (
    nextCards: CardFeedCard[],
    source: 'assign all' | 'snackbar',
  ): void => {
    if (nextCards.length === 0) return
    setFlyoutCards(nextCards)
    setIsFlyoutOpen(true)
    analytics.track('card feed details', {
      action: 'assign flyout opened',
      source,
      count: `${nextCards.length}`,
    })
  }

  const handleAssignAllClick = (): void => {
    setSelectAllSignal((s) => (s ?? 0) + 1)
    openFlyoutWith(unassignedCards, 'assign all')
  }

  const handleCloseFlyout = (): void => {
    setIsFlyoutOpen(false)
  }

  const handleAssignFromRow = (
    cards: CardFeedCard[],
    selection: CardholderSelection,
  ): void => {
    setConfirmState({ cards, recipient: toRecipient(selection) })
    analytics.track('card feed details', {
      action: 'assign confirm opened',
      source: 'row dropdown',
      count: `${cards.length}`,
    })
  }

  const handleReinvite = (card: CardFeedCard): void => {
    // The row menu has no pending state, so guard here against a second
    // click firing a duplicate invite while one is still in flight.
    if (reinviteCardholder.isPending) return
    analytics.track('card feed details - reinvite')
    const organizationId = user?.organizationId
    const assigneeEmail = card.assignedUser?.email
    if (!organizationId || !assigneeEmail) {
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: <ReinviteCardholderErrorToastMessage />,
        }),
      )
      return
    }
    reinviteCardholder.mutate(
      {
        organizationId,
        body: {
          emails: [assigneeEmail],
          // Always Card Manager: the assign use case sets every assignee's
          // pending invite to CARD_MANAGER (see EX-39737), so no other role is
          // reachable here. If EX-39737 changes that to preserve Admin/Owner
          // invites, source the role from the invite instead of hardcoding.
          role: OrganizationRoles.cardManager,
          cardManagerAccounts: [card.id],
        },
      },
      {
        onSuccess: () => {
          analytics.track('card feed details', { action: 'card reinvited' })
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <ReinviteCardholderSuccessToastMessage
                  recipient={
                    assignedUserDisplayName(card.assignedUser) ?? assigneeEmail
                  }
                />
              ),
            }),
          )
        },
        onError: () => {
          analytics.track('card feed details', {
            action: 'card reinvited failure',
          })
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: <ReinviteCardholderErrorToastMessage />,
            }),
          )
        },
      },
    )
  }

  const handleUnassign = (card: CardFeedCard): void => {
    analytics.track('card feed details - unassign')
    setUnassignCard(card)
  }

  const handleAssigned = (): void => {
    // The assigned cards flip to ASSIGNED, so clear the table's selection.
    setSelectionResetSignal((signal) => signal + 1)
    setIsFlyoutOpen(false)
    setConfirmState(null)
  }

  // Only a connected feed can take assignments, so the action is omitted
  // entirely for non-active feeds rather than shown in a permanently-disabled
  // state.
  const actions = isActive ? (
    <Button
      variant="primary"
      onClick={handleAssignAllClick}
      disabled={!canAssign}
      data-testid="card-feed-details-assign-all"
    >
      {translate('Assign All')}
    </Button>
  ) : undefined

  return (
    <>
      <Helmet>
        <title>{translate('Card Feed')}</title>
      </Helmet>
      <LayoutPage
        title={title}
        subtitle={isPending || isFailed ? undefined : <Subtitle />}
        actions={actions}
      >
        <CardFeedCardsTable
          feedId={id}
          onAssignRequest={(c): void => openFlyoutWith(c, 'snackbar')}
          onAssignFromRow={handleAssignFromRow}
          onReinvite={handleReinvite}
          onUnassign={handleUnassign}
          resetSelectionSignal={selectionResetSignal}
          selectAllSignal={selectAllSignal}
          isActive={isActive}
          isPending={isPending}
          isFailed={isFailed}
        />
      </LayoutPage>
      <AssignAccountOwnerFlyout
        isOpen={isFlyoutOpen}
        onClose={handleCloseFlyout}
        cards={flyoutCards}
        cardFeedId={id}
        onAssigned={handleAssigned}
      />
      {confirmState && (
        <ConfirmCardAssignmentModal
          isOpen
          onClose={(): void => setConfirmState(null)}
          recipient={confirmState.recipient}
          cards={confirmState.cards}
          cardFeedId={id}
          onAssigned={handleAssigned}
        />
      )}
      {unassignCard && (
        <ConfirmUnassignCardModal
          isOpen
          onClose={(): void => setUnassignCard(null)}
          card={unassignCard}
          cardFeedId={id}
        />
      )}
    </>
  )
}

const Subtitle: React.FC = () => {
  const translate = useTranslate()
  const { APP_NAME: rawAppName } = useCurrentBrand()
  return (
    <span css={style.subtitle}>
      {translate(
        'Active cards will appear in your ${cardsPageLink} once card holders have been assigned to your Org in ${appName}.',
        {
          cardsPageLink: (
            <HoverableLink
              to={cardsPhysicalCardsUrl()}
              regular
              underline
              size={14}
              ariaLabelSource="cards page"
              testId="card-feed-details-cards-page-link"
            >
              {translate('cards page')}
            </HoverableLink>
          ),
          appName: translate(rawAppName),
        },
      )}
    </span>
  )
}

export default CardFeedDetails
